import React from 'react';
import { CheckCircle } from 'lucide-react';
import NewsletterSection from '../components/NewsletterSection';

const ProgrammesPage = () => (
  <>
    {/* Hero */}
    <section style={{ paddingTop: '120px', paddingBottom: '4rem', background: 'var(--off-white)' }}>
      <div className="container">
        <div className="section-header" style={{ marginBottom: '2rem' }}>
          <span className="section-tag">Our Work</span>
          <h1>Programmes & Initiatives</h1>
          <p>
            At Realm of Love Foundation, our programmes are designed to deliver deep, sustainable, and dignified
            impact within under-resourced communities. We believe that meaningful change happens when interventions
            are consistent, holistic, and community-centred, rather than fragmented.
          </p>
        </div>
      </div>
    </section>

    {/* School-centred model intro */}
    <section style={{ background: 'var(--navy)', padding: '3rem 0' }}>
      <div className="container">
        <div style={{ maxWidth: '760px', margin: '0 auto', textAlign: 'center' }}>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '1.05rem', lineHeight: 1.8, marginBottom: '1rem' }}>
            To achieve this, the Foundation has adopted a <strong style={{ color: '#fff' }}>school-centred impact model</strong>,
            recognising schools as powerful hubs through which children, families, educators, and surrounding communities
            can be reached effectively.
          </p>
          <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.95rem' }}>
            Our work focuses on <strong style={{ color: 'var(--teal)' }}>education</strong>,{' '}
            <strong style={{ color: 'var(--rose)' }}>health</strong>,{' '}
            <strong style={{ color: 'var(--gold)' }}>dignity</strong>, and{' '}
            <strong style={{ color: '#fff' }}>inclusion</strong> — ensuring that no learner or community member is
            left behind due to socio-economic circumstances.
          </p>
        </div>
      </div>
    </section>

    {/* Programmes list */}
    <section className="section">
      <div className="container">
        {[
          {
            number: '01',
            title: 'Adopt-a-School Impact Programme',
            subtitle: 'Cornerstone Programme',
            cat: 'Education',
            catClass: 'cat-education',
            image: '/img/DSC_1916.jpg',
            color: 'var(--teal)',
            desc: 'The Adopt-a-School Impact Programme is Realm of Love Foundation\'s long-term commitment to partnering with selected public schools in under-resourced communities to deliver sustained and measurable impact. Rather than spreading resources thinly across multiple locations, the Foundation adopts a school as a focal point, allowing for deeper engagement, stronger collaboration with school leadership, and long-term support for learners and families.',
            features: [
              'Partners closely with school management and educators',
              'Identifies priority needs within the school environment',
              'Delivers targeted support to learners from less-privileged backgrounds',
              'Ensures dignity, inclusion, and equity among learners',
              'Serves as a community hub for education, wellness, and reading programmes',
            ],
          },
          {
            number: '02',
            title: 'Healthier Future Community Programme',
            subtitle: 'Flagship Programme',
            cat: 'Wellness',
            catClass: 'cat-wellness',
            image: '/img/wellness.jpg',
            color: 'var(--rose)',
            desc: 'The Healthier Future Community Programme is our flagship initiative, designed to improve the nutritional, physical, mental, emotional, and overall wellbeing of individuals and families within under-resourced communities. Recognising that healthy children are raised in healthy environments, this programme adopts a community-wide approach to wellness — reaching learners, parents, caregivers, educators, and community members through practical, culturally relevant workshops led by health professionals.',
            features: [
              'Nutrition education and healthy lifestyle awareness',
              'Mental and emotional wellbeing education',
              'Stress management and self-care',
              'Hygiene, dignity, and personal wellbeing',
              'Child wellness and the link between health and learning',
              'Inclusive health conversations, including neurodiversity awareness',
            ],
          },
          {
            number: '03',
            title: 'Back-to-School Essentials Drive',
            subtitle: 'Annual Programme',
            cat: 'Community',
            catClass: 'cat-community',
            image: '/img/excellence.jpg',
            color: 'var(--gold)',
            desc: 'The Back-to-School Essentials Drive is an annual initiative delivered through the Foundation\'s Adopt-a-School Impact Programme. The drive aims to reduce barriers to learning by providing essential school items to learners from less-privileged backgrounds, ensuring that no child feels excluded or inadequate among their peers due to a lack of basic resources.',
            features: [
              'School bags for learners in need',
              'Stationery and learning materials',
              'Essential school-related items identified by the school',
              'Promotes dignity, confidence, and equality among learners',
            ],
          },
          {
            number: '04',
            title: 'Power of Reading Initiative',
            subtitle: 'Literacy & Early Learning Programme',
            cat: 'Education',
            catClass: 'cat-education',
            image: '/img/image_2.jpg',
            color: 'var(--teal)',
            desc: 'The Power of Reading Initiative is a literacy-focused programme aimed at nurturing a love for reading among early-grade learners. The programme recognises literacy as a foundational skill that influences academic success, confidence, and long-term educational outcomes.',
            features: [
              'Guided reading sessions in adopted schools',
              'Age-appropriate reading materials provided',
              'Encourages early literacy development',
              'Supports comprehension, imagination, and language skills',
              'Aligned with broader education support efforts',
            ],
          },
        ].map((prog, i) => (
          <div
            key={i}
            className={`scroll-animate programme-row ${i % 2 === 1 ? 'programme-row-reverse' : ''}`}
            style={{ marginBottom: '4rem', paddingBottom: '4rem', borderBottom: i < 3 ? '1px solid var(--gray-100)' : 'none' }}
          >
            {/* Image */}
            <div style={{ position: 'relative', height: '360px', borderRadius: 'var(--radius-xl)', overflow: 'hidden' }}>
              <img src={prog.image} alt={prog.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <div style={{
                position: 'absolute', top: '1rem', left: '1rem',
                background: prog.color, color: '#fff',
                fontFamily: 'var(--font-heading)', fontWeight: 800,
                fontSize: '1.75rem', lineHeight: 1,
                width: '56px', height: '56px', borderRadius: '12px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: 0.9,
              }}>
                {prog.number}
              </div>
            </div>

            {/* Content */}
            <div>
              <span className={`programme-card-category ${prog.catClass}`}>{prog.cat}</span>
              <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', fontWeight: 500, marginTop: '0.5rem', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {prog.subtitle}
              </div>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>{prog.title}</h3>
              <p style={{ marginBottom: '1.5rem', fontSize: '0.95rem', lineHeight: 1.7 }}>{prog.desc}</p>
              <ul style={{ marginBottom: '0' }}>
                {prog.features.map((f, j) => (
                  <li key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.35rem 0', fontSize: '0.9rem', color: 'var(--gray-600)' }}>
                    <CheckCircle size={16} style={{ color: prog.color, flexShrink: 0, marginTop: '2px' }} /> {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </section>

    {/* Our Approach */}
    <section style={{ background: 'var(--off-white)', padding: '5rem 0' }}>
      <div className="container">
        <div className="section-header">
          <span className="section-tag">Our Approach</span>
          <h2>What Guides Us</h2>
        </div>
        <div className="grid grid-4">
          {[
            { label: 'Dignity', desc: 'Ensuring beneficiaries feel respected and valued in every interaction.', color: 'var(--rose)' },
            { label: 'Equity', desc: 'Addressing inequality with fairness and compassion at every level.', color: 'var(--teal)' },
            { label: 'Inclusion', desc: 'Supporting diverse needs, including hidden disabilities and neurodiversity.', color: 'var(--navy)' },
            { label: 'Sustainability', desc: 'Prioritising long-term impact over once-off interventions.', color: 'var(--gold)' },
          ].map((v, i) => (
            <div key={i} className="scroll-animate card" style={{ padding: '2rem', textAlign: 'center', transitionDelay: `${i * 100}ms` }}>
              {/* <div style={{
                width: '16px', height: '4px', borderRadius: '2px',
                background: v.color, margin: '0 auto 1rem',
              }} /> */}
              <h4 style={{ marginBottom: '0.5rem', color: v.color }}>{v.label}</h4>
              <p style={{ fontSize: '0.875rem' }}>{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Commitment quotes */}
    <section style={{ padding: '5rem 0', background: '#fff' }}>
      <div className="container">
        <div className="section-header" style={{ marginBottom: '3rem' }}>
          <span className="section-tag">Our Commitment</span>
          <h2>We believe that when schools are strengthened, communities are uplifted.</h2>
          <p>When health, education, and dignity are prioritised together, lasting change becomes possible.</p>
        </div>
        <div className="grid grid-2" style={{ gap: '1.5rem' }}>
          {[
            '"We believe wellness is not individual, it is communal. When families are healthy, children thrive, schools perform better, and communities rise."',
            '"When you strengthen a school, you strengthen an entire community — and when you prioritise a child\'s health and dignity, you unlock their full potential."',
          ].map((quote, i) => (
            <div key={i} className="scroll-animate card" style={{
              padding: '2.5rem',
              borderLeft: '4px solid var(--teal)',
              background: 'var(--off-white)',
            }}>
              <p style={{ fontSize: '1.05rem', lineHeight: 1.8, color: 'var(--gray-700)', fontStyle: 'italic' }}>{quote}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <NewsletterSection />
  </>
);

export default ProgrammesPage;
